import type {
  CategoryListResponse,
  DocSummary,
  PaginatedDocList,
  SingleDoc,
  SolutionAreaListResponse,
} from "./types.js";

import { api } from "../../lib/api.js";
import { env } from "../../lib/env.js";

/**
 * API client for the Documentation section.
 *
 * Every call goes to this app's BFF at `/api/documentation/*`, which relays
 * to portal-api's curated `/api/v1/reseller/*` namespace. The path shape is
 * the command center's own, deliberately, so the reader pages ported from it
 * needed their import paths changed and not their call sites.
 *
 * READ-ONLY, and that is a property of the whole stack rather than a
 * convention here: portal-api's reseller namespace exposes no write to call,
 * and the BFF declares only GET routes. There is no `createDoc` below to
 * delete later — the file simply never had one.
 *
 * The session rides as this origin's httpOnly cookie (`credentials:
 * "include"`, set by `lib/api.ts`), so nothing on this page holds a token.
 */

const BASE = "/api/documentation";

export interface ListDocsArgs {
  q?: string;
  category_id?: string;
  release_id?: string;
  solution_area?: string;
  page?: number;
  per_page?: number;
}

/**
 * Note there is no `status` argument, unlike the command center's client.
 * A reseller sees published documents and nothing else, so a status filter
 * would be a control whose only settings are "the one you get" and "an empty
 * page". The BFF drops the parameter too, in case anything sends it.
 */
export function listDocs(args: ListDocsArgs): Promise<PaginatedDocList> {
  const qs = new URLSearchParams();
  if (args.q) qs.set("q", args.q);
  if (args.category_id && args.category_id !== "all") {
    qs.set("category_id", args.category_id);
  }
  if (args.release_id) qs.set("release_id", args.release_id);
  if (args.solution_area && args.solution_area !== "all") {
    qs.set("solution_area", args.solution_area);
  }
  qs.set("page", String(args.page ?? 1));
  qs.set("per_page", String(args.per_page ?? 15));
  return api<PaginatedDocList>(`${BASE}/documents?${qs.toString()}`);
}

/**
 * Fetch the COMPLETE document list by walking every page.
 *
 * portal-api hard-caps `per_page` at 100 (`BaseController#paginate` does
 * `[per_page, 100].min`), so asking for 500 silently truncates to the first
 * 100. The sidebar tallies its per-category and per-release counts from this
 * list, and a truncated pull makes every folder under-count — the command
 * center hit exactly that (a 17-doc folder reading "2"). Paginate to
 * completion rather than trusting one wide page.
 */
export async function listAllDocs(
  args: Omit<ListDocsArgs, "page" | "per_page"> = {},
): Promise<DocSummary[]> {
  const perPage = 100; // the server's hard ceiling — the biggest legal page
  const first = await listDocs({ ...args, page: 1, per_page: perPage });
  const out = [...first.data];
  const totalPages = Math.max(1, first.meta.total_pages);
  for (let page = 2; page <= totalPages; page += 1) {
    const next = await listDocs({ ...args, page, per_page: perPage });
    out.push(...next.data);
  }
  return out;
}

export function getDoc(idOrSlug: string | number): Promise<SingleDoc> {
  return api<SingleDoc>(`${BASE}/documents/${encodeURIComponent(String(idOrSlug))}`);
}

export function listCategories(): Promise<CategoryListResponse> {
  return api<CategoryListResponse>(`${BASE}/categories`);
}

export function listSolutionAreas(): Promise<SolutionAreaListResponse> {
  return api<SolutionAreaListResponse>(`${BASE}/solution-areas`);
}

/**
 * Raw bytes of a document's primary attachment, through the BFF's binary
 * relay rather than the storage URL the serializer carries.
 *
 * Routing it through this origin is not a nicety. The storage URL is
 * cross-origin (DigitalOcean Spaces), which taints the canvas the PDF
 * viewer draws pages onto, and it would need a credential this app is not
 * going to put in the page. Used by the "Download PDF" builder (`docPdf.ts`)
 * for its image / text / pdf-passthrough modes.
 */
export async function fetchDocFileBytes(
  docId: number,
): Promise<{ bytes: ArrayBuffer; contentType: string | null }> {
  return fetchDocBytes(`${BASE}/documents/${docId}/file`);
}

/** Bytes of the doc's faithful ORIGINAL PDF (`source_file`) — the "Download
 * original" on a document transcribed from a whitepaper or datasheet. Same
 * relay, same reasoning. */
export async function fetchDocSourceFileBytes(
  docId: number,
): Promise<{ bytes: ArrayBuffer; contentType: string | null }> {
  return fetchDocBytes(`${BASE}/documents/${docId}/source-file`);
}

async function fetchDocBytes(
  path: string,
): Promise<{ bytes: ArrayBuffer; contentType: string | null }> {
  const res = await fetch(`${env.apiUrl}${path}`, { credentials: "include" });

  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const parsed = (await res.json()) as { message?: string; error?: string };
      // The BFF sends `{ error, code, message }`; `message` is the sentence
      // written for a person, so prefer it over the machine-readable code.
      if (parsed.message) msg = parsed.message;
      else if (parsed.error) msg = parsed.error;
    } catch {
      /* non-JSON error body — keep the HTTP status message */
    }
    throw new Error(msg);
  }

  return { bytes: await res.arrayBuffer(), contentType: res.headers.get("content-type") };
}
